import { ReactComponent as FancyTrashIcon } from 'assets/garbage.svg';
import { ReactComponent as CheckmarkIcon } from 'assets/icons/green-check-circle.svg';
import { ReactComponent as PathIcon } from 'assets/paths-icon.svg';
import { ReactComponent as RedInfoIcon } from 'assets/red-info.svg';
import { ReactComponent as TopWidgetArrow } from 'assets/top-widget-arrow.svg';
import classnames from 'classnames';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ButtonTagsForm } from '../../../editor/features/build/components/widgets/buttons/ButtonConfigform/ButtonTagsForm';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import ButtonModel, {
  GenericButtonModel,
  OptInButtonConfig,
} from '@mm/state/model/ButtonModel';
import TagModel from '@mm/state/model/TagModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { hasSMSNumberSelector } from '@mm/state/selectors/twillio';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import { InputWithEmoji } from '../../components/InputWithEmoji/InputWithEmoji';
import { PrimaryToolTip } from '../../components/PrimaryToolTip/PrimaryToolTip';
import { useButtonHandlers } from '../../helpers/widget-helpers/use-button-handlers';
import { ActivePathType } from '../common/ActivePathSelection/ActivePathSelection';
import { PathGraphContext } from '../common/PathGraph/PathGraph';
import './OptInButton.pcss';
import PrimarySelect from '../../components/PrimarySelect/PrimarySelect';
import { StoreState } from '@mm/state/store-interfaces';
import { generatePageStructure } from '@mm/state/helpers/filterable-select-helpers';

type Props = {
  widget: WidgetModel;
  button: GenericButtonModel<OptInButtonConfig>;
  openedButtonId: number | undefined;
  setOpenedButtonId: (openedButtonId: number | undefined) => void;
  setActivePathType: (activePathType: ActivePathType) => void;
  setActivePageId: (id: number | undefined) => void;
  deleteable: boolean;
};

export const OptInButton = (props: Props) => {
  const {
    button,
    widget,
    setOpenedButtonId,
    openedButtonId,
    setActivePathType,
    setActivePageId,
    deleteable,
  } = props;

  const buttonHandlers = useButtonHandlers();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const smsActivated = useSelector(hasSMSNumberSelector);

  const [buttonName, setButtonName] = useState('');
  const [smsResponse, setSmsResponse] = useState('');
  const [pageId, setPageId] = useState(button.config?.pageId || undefined);
  const [tags, setTags] = useState<TagModel[]>(button.tags || []);
  const pages = useSelector((state: StoreState) => state.page.entities);
  const pageGroups = useSelector(
    (state: StoreState) => state.pageGroup.entities,
  );
  const noOption = { name: 'No', id: -1, pageGroupId: -1, kind: null as any };
  const selectedPage = pages.find(p => p.id === pageId) || noOption;
  const selectorPages = [...pages, noOption];
  const organizedPageOptions = useMemo(
    () => generatePageStructure(selectorPages, pageGroups, true),
    [selectorPages, pageGroups],
  );

  const { setActiveNode, deselectWidgetButtons } = useContext(PathGraphContext);

  useEffect(() => {
    setButtonName(button.name);
    setSmsResponse(button.config.smsResponse);
    setTags(button.tags || []);
    setPageId(button.config.pageId || undefined);
  }, [button, openedButtonId]);

  const openButton = () => {
    deselectWidgetButtons?.(widget.buttons);
    setOpenedButtonId(button.id);
    setActivePageId(pageId);
    setActiveNode?.({
      id: button.id,
      type: 'button',
      dialogueId: pageId,
      action: 'open',
    });
    setActivePathType(
      button.config.kind === 'facebook' ? 'facebook' : 'regular',
    );
  };
  const onSelectPage = ({ id }: { id: number }) => {
    setPageId(id);
    setActivePageId(id);
    setActiveNode?.({
      id: button.id,
      type: 'button',
      dialogueId: id,
      action: 'open',
    });
  };
  const isHidden = (button: ButtonModel) =>
    (button.config as OptInButtonConfig).hidden;
  const anyHiddenButtons = widget.buttons.some(isHidden);
  const otherButtonsAllHidden = widget.buttons
    .filter(
      b =>
        b.id !== button.id && (b.config as OptInButtonConfig).kind !== 'text',
    )
    .every(isHidden);

  const toggleHidden = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (otherButtonsAllHidden) {
      return;
    }
    buttonHandlers.updateButton(widget, {
      ...button,
      config: { ...button.config, hidden: !button.config.hidden, tags },
    });
  };
  const onClose = () => {
    setOpenedButtonId(undefined);
    deselectWidgetButtons?.(widget.buttons);
  };

  const saveButton = () => {
    const updatedButton: GenericButtonModel<OptInButtonConfig> = {
      ...button,
      name: buttonName,
      tags,
    };
    switch (button.config.kind) {
      case 'sms':
        updatedButton.config = { ...updatedButton.config, smsResponse };
        break;
      case 'text':
        updatedButton.config = {
          ...updatedButton.config,
          pageId: !pageId || pageId === -1 ? null : pageId,
        };
        break;
      default:
    }
    buttonHandlers.updateButton(widget, updatedButton);
    onClose();
  };

  const deleteButton = () => {
    buttonHandlers.delete(widget.id, widget.pageId, button);
    setOpenedButtonId(undefined);
  };

  const renderSmsButtonArea = () => {
    if (!smsActivated) {
      return (
        <div className="opt-in-button__sms-not-activated-alert">
          <RedInfoIcon className="opt-in-button__sms-not-activated-alert-icon" />
          Phone number is not activated.&nbsp;
          <Link
            to={BotBuilderRoutes.settingsSmsActivate.getLink({ chatbotId })}
          >
            Activate SMS
          </Link>
        </div>
      );
    }
    return (
      <>
        <div className="opt-in-button__sms-response-input-heading">
          SMS opt-in confirmation
          <PrimaryToolTip
            anchorElement={<i className="far fa-question-circle" />}
          >
            SMS message to be sent to user’s phone after they opt in
          </PrimaryToolTip>
        </div>
        <InputWithEmoji
          className="opt-in-button__text-input-with-emoji-picker"
          textInputClassName="opt-in-button__text-input"
          multiline
          value={smsResponse}
          onChange={setSmsResponse}
        />
      </>
    );
  };

  const renderFacebookButtonArea = () => {
    return (
      <div className="opt-in-button__facebook-checkbox-items">
        <div className="opt-in-button__facebook-checkbox-item">
          <CheckmarkIcon className="opt-in-button__facebook-checkbox-item-checkbox" />
          Send to Messenger for opt-in
        </div>
        <div className="opt-in-button__facebook-checkbox-item">
          <CheckmarkIcon className="opt-in-button__facebook-checkbox-item-checkbox" />
          Conversation will continue in Messenger after visitor opts in
        </div>
      </div>
    );
  };

  return (
    <div className="opt-in-button">
      {openedButtonId === button.id && (
        <div className="opt-in-button__edit-area">
          <div className="opt-in-button__edit-area-bubble">
            <InputWithEmoji
              className="opt-in-button__text-input-with-emoji-picker"
              textInputClassName="opt-in-button__text-input"
              value={buttonName}
              onChange={setButtonName}
            />

            {button.config.kind === 'sms' && renderSmsButtonArea()}
            {button.config.kind === 'facebook' && renderFacebookButtonArea()}
            {(button.config.kind === 'text' || pageId) && (
              <div className="opt-in-button__associated-dialogue">
                <div className="opt-in-button__associated-dialogue-text">
                  Take users to a new Dialogue
                </div>

                <div className="opt-in-button__associated-dialogue-input-wrapper">
                  <PrimarySelect
                    showSearchFilter={true}
                    placeholder="No"
                    className="opt-in-button__associated-dialogue-input"
                    disabled={button.config.kind !== 'text'}
                    onChange={onSelectPage}
                    organizedOptions={organizedPageOptions}
                    value={selectedPage}
                  />
                </div>
              </div>
            )}
            <div className="opt-in-button__tags-wrapper">
              <ButtonTagsForm
                title="Add a Tag to contacts who click this button"
                kind="add_tags"
                tags={tags}
                onTagAdd={(tag: TagModel) =>
                  setTags(Array.from(new Set([tag].concat(tags))))
                }
                onTagRemove={(tag: TagModel) =>
                  setTags(tags.filter(t => t.name !== tag.name))
                }
              />
            </div>
            <div className="opt-in-button__edit-area-buttons">
              <PrimaryButton
                className="opt-in-button__edit-area-button"
                color={ButtonColor.secondaryBlue}
                onClick={onClose}
              >
                Cancel
              </PrimaryButton>
              <PrimaryButton
                className="opt-in-button__edit-area-button"
                onClick={saveButton}
              >
                Save
              </PrimaryButton>
            </div>
          </div>
          <TopWidgetArrow className="opt-in-button__edit-area-bottom-arrow" />
        </div>
      )}

      <div
        onClick={openButton}
        className={classnames('opt-in-button__button', {
          'opt-in-button__button--selected': openedButtonId === button.id,
          'opt-in-button__button--hidden': button.config.hidden,
        })}
      >
        <span className="opt-in-button__button-text">{button.name}</span>

        <div className="opt-in-button__icon-container">
          {button.config.pageId && (
            <PathIcon className="opt-in-button__path-icon" />
          )}
          {anyHiddenButtons && (
            <span
              onClick={toggleHidden}
              className={classnames('opt-in-button__button-hidden-status', {
                'opt-in-button__button-hidden-status--disabled':
                  otherButtonsAllHidden && !button.config.hidden,
              })}
            >
              {button.config.hidden ? (
                <i className="far fa-eye-slash" />
              ) : (
                <i className="far fa-eye" />
              )}
            </span>
          )}
          {deleteable && (
            <FancyTrashIcon
              className="opt-in-button__delete-icon"
              onClick={deleteButton}
            />
          )}
        </div>
      </div>
    </div>
  );
};
